import { DataApi } from '@utils/data';
import { useCallback, useMemo, useState } from 'react';
import { MediaContent } from '../types/types';
import { VideoType } from '@features/dashboard/widgets/videos/types';
import { debounce } from 'lodash';

export const useFreemiumVideos = (videoLoader: DataApi<MediaContent>) => {
  const [videos, setVideos] = useState<MediaContent[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const loadMoreVideos = useCallback(async () => {
    if (isLoading || !hasMore) return;
    setIsLoading(true);
    try {
      const newVideos = await videoLoader?.loadData();
      setVideos(prev => {
        const existingIds = new Set(prev.map(video => video.id));
        const uniqueNewVideos = newVideos?.filter(video => !existingIds.has(video.id));
        return [...prev, ...(uniqueNewVideos || [])];
      });
      setHasMore(videoLoader.hasMoreData());
    } catch (error) {
      console.error('Error loading more videos:', error);
    } finally {
      setIsLoading(false);
    }
  }, [videoLoader, isLoading, hasMore]);

  const loadShorts = useCallback(async () => {
    if (!videoLoader) return;
    videoLoader.addFilter({ video_type: VideoType.SHORTS });
    const shorts = await videoLoader.loadData();
    setVideos(shorts);
    setHasMore(videoLoader.hasMoreData());
  }, [videoLoader]);

  const loadVideosFromQuery = useCallback(
    async (query: Record<string, any>) => {
      videoLoader.resetQuery(query);
      const newVideos = await videoLoader.loadData();
      setVideos(newVideos);
      setHasMore(videoLoader.hasMoreData());
    },
    [videoLoader, loadMoreVideos]
  );

  const resetVideos = useCallback(() => {
    if (!videoLoader) return;
    videoLoader.resetQuery({});
    setVideos([]);
    setHasMore(true);
  }, [videoLoader]);

  const loadInitialVideos = useCallback(async () => {
    resetVideos();
    await loadMoreVideos();
  }, [resetVideos, loadMoreVideos]);

  const performSearch = useCallback(
    async (query: string) => {
      if (!videoLoader) return;
      videoLoader.addFilter({ search: query });
      const newVideos = await videoLoader.loadData();
      setVideos(newVideos);
      setHasMore(videoLoader.hasMoreData());
    },
    [videoLoader]
  );

  const searchVideos = useMemo(() => debounce(performSearch, 500), [performSearch]);

  return {
    videos,
    isLoading,
    hasMore,
    loadMoreVideos,
    loadInitialVideos,
    resetVideos,
    loadVideosFromQuery,
    loadShorts,
    searchVideos,
  };
};
